import { Injectable } from '@angular/core';

/**
 * One layout read per frame for the whole page, instead of one per block.
 *
 * Every block re-measures its own rendered height whenever the store hands it a
 * fresh entity — see {@link BaseBlockComponent}. Left to itself each block would
 * schedule that read on its own timer, and because the measurement feeds a signal
 * that drives the block's own host classes, the reads and the writes interleave:
 * read, style, write, read again on an invalidated layout. On a page of forty
 * blocks that is forty forced reflows, and under zone.js forty application ticks,
 * for what is a single frame's worth of work.
 *
 * Collecting the callbacks and running them together in one animation frame keeps
 * the reads back to back. Signal writes do not touch the DOM synchronously, so
 * nothing invalidates layout in between and the browser lays out once.
 *
 * Keyed by the caller rather than queued: a block that re-measures twice before
 * the frame arrives wants the later measurement, not both.
 */
@Injectable({ providedIn: 'root' })
export class BlockMeasureScheduler {
    private readonly pending = new Map<object, () => void>();
    private frame: number | null = null;

    /** Replaces anything `owner` had queued for the upcoming frame. */
    public schedule(owner: object, measure: () => void): void {
        this.pending.set(owner, measure);

        if (this.frame !== null) {
            return;
        }

        this.frame = requestAnimationFrame(() => this.flush());
    }

    /**
     * Drops a queued measurement. Callers cancel on teardown: the read goes
     * through `viewChild.required`, which throws once the view is gone.
     *
     * The frame itself is left standing even when this empties the queue —
     * cancelling it would only mean the next caller immediately asks for another,
     * and a flush with nothing due does nothing.
     */
    public cancel(owner: object): void {
        this.pending.delete(owner);
    }

    private flush(): void {
        this.frame = null;

        // Drained before the first callback runs: a measurement is free to queue
        // the next one, and that belongs to the following frame.
        const due = [...this.pending.values()];
        this.pending.clear();

        due.forEach((measure) => measure());
    }
}
